export type NutrientStatus = "known" | "unknown" | "partial";

export interface NutrientTotal {
  value: number;
  status: NutrientStatus;
}

export interface MealNutritionAgg {
  calories: NutrientTotal;
  protein: NutrientTotal;
  carbs: NutrientTotal;
  fat: NutrientTotal;
  sugar: NutrientTotal;
  fiber: NutrientTotal;
  mealCount: number;
  hasAnyFood: boolean;
}

function toNutrientTotal(val: unknown): NutrientTotal {
  if (val === null || val === undefined) return { value: 0, status: "unknown" };
  const n = typeof val === "string" ? Number(val) : val;
  if (typeof n !== "number" || !Number.isFinite(n) || n < 0) {
    return { value: 0, status: "unknown" };
  }
  return { value: n, status: "known" };
}

function addInto(acc: NutrientTotal, val: unknown): NutrientTotal {
  const t = toNutrientTotal(val);
  if (t.status === "unknown") return { value: acc.value, status: acc.status === "known" ? "partial" : acc.status };
  return { value: acc.value + t.value, status: acc.status === "unknown" ? "known" : acc.status };
}

export function createEmptyAgg(): MealNutritionAgg {
  return {
    calories: { value: 0, status: "unknown" },
    protein: { value: 0, status: "unknown" },
    carbs: { value: 0, status: "unknown" },
    fat: { value: 0, status: "unknown" },
    sugar: { value: 0, status: "unknown" },
    fiber: { value: 0, status: "unknown" },
    mealCount: 0,
    hasAnyFood: false,
  };
}

export function addMealNutrition(
  agg: MealNutritionAgg,
  meal: {
    calories?: number | null;
    protein_g?: number | null;
    carbs_g?: number | null;
    fat_g?: number | null;
    fiber_g?: number | null;
    sugar_g?: number | null;
  },
): MealNutritionAgg {
  agg.calories = addInto(agg.calories, meal.calories);
  agg.protein = addInto(agg.protein, meal.protein_g);
  agg.carbs = addInto(agg.carbs, meal.carbs_g);
  agg.fat = addInto(agg.fat, meal.fat_g);
  agg.fiber = addInto(agg.fiber, meal.fiber_g);
  agg.sugar = addInto(agg.sugar, meal.sugar_g);
  agg.mealCount += 1;
  agg.hasAnyFood = true;
  return agg;
}

export function addScanNutrition(
  agg: MealNutritionAgg,
  scan: {
    calories?: number | null;
    recipe_data?: { nutrition?: { protein?: number; carbs?: number; fat?: number; sugar?: number; fiber?: number } } | null;
  },
): MealNutritionAgg {
  agg.calories = addInto(agg.calories, scan.calories);
  const n = scan.recipe_data?.nutrition;
  if (n) {
    agg.protein = addInto(agg.protein, n.protein);
    agg.carbs = addInto(agg.carbs, n.carbs);
    agg.fat = addInto(agg.fat, n.fat);
    agg.sugar = addInto(agg.sugar, n.sugar);
    agg.fiber = addInto(agg.fiber, n.fiber);
  }
  agg.mealCount += 1;
  agg.hasAnyFood = true;
  return agg;
}

export function isKnownZero(t: NutrientTotal): boolean {
  return t.status === "known" && t.value === 0;
}

export function isUnknown(t: NutrientTotal): boolean {
  return t.status === "unknown";
}

export function isFullyKnown(t: NutrientTotal): boolean {
  return t.status === "known";
}

export function isPartial(a: MealNutritionAgg): boolean {
  if (
    a.calories.status === "partial" ||
    a.protein.status === "partial" ||
    a.carbs.status === "partial" ||
    a.fat.status === "partial" ||
    a.sugar.status === "partial" ||
    a.fiber.status === "partial"
  ) return true;
  if (!a.hasAnyFood) return false;
  const statuses = [a.calories, a.protein, a.carbs, a.fat, a.sugar, a.fiber].map(n => n.status);
  const hasKnown = statuses.includes("known");
  const hasUnknown = statuses.includes("unknown");
  return hasKnown && hasUnknown;
}

export function localDateString(d: Date = new Date()): string {
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 10);
}

export function localDayRange(dateStr: string): { start: string; end: string } {
  return {
    start: `${dateStr}T00:00:00`,
    end: `${dateStr}T23:59:59`,
  };
}
